import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { IonIcon, IonLabel, IonRouterOutlet, IonTabBar, IonTabButton, IonTabs } from '@ionic/react';
import { ellipsisHorizontal } from 'ionicons/icons';
import { Redirect, Route, useLocation } from 'react-router-dom';
import { SU_TABS } from '../components/BottomTabBar';
import MoreSheet from '../components/MoreSheet';
import HomePage from '../pages/HomePage';
import NetworkPage from '../pages/NetworkPage';
import StateWisePage from '../pages/StateWisePage';
import StoresPage from '../pages/StoresPage';
import StoreDetailPage from '../pages/StoreDetailPage';
import { useInitOrg, useOrg } from '../hooks/useOrg';
import { MoreSheetContext } from '../hooks/useMoreSheet';
import { useTheme, themeClass } from '../theme/useTheme';
import '../styles/su-theme.css';

/**
 * "More" opens a sheet instead of a page, so it must not navigate. IonTabBar only accepts tab buttons (or components
 * flagged `isTabButton`) and injects href/onClick into them; this one ignores both and handles the tap itself.
 */
const MoreTabButton: React.FC<{ tab: string; open: boolean; onToggle: () => void; badge: string }> & { isTabButton?: boolean } = ({
  open, onToggle, badge,
}) => (
  <IonTabButton
    tab="more"
    className={open ? 'su-more-open' : undefined}
    {...({ onIonTabButtonClick: onToggle } as any)}
  >
    <span className="su-more-icon">
      <IonIcon icon={ellipsisHorizontal} />
      <span className="su-org-badge" data-testid="su-org-badge">{badge}</span>
    </span>
    <IonLabel>More</IonLabel>
  </IonTabButton>
);
MoreTabButton.isTabButton = true;

/**
 * New SU / ETH dashboard shell, mounted at /app. Rendered OUTSIDE the legacy
 * IonSplitPane/IonPage tree (see App.js) so it owns its own router outlet.
 */
const SuRoutes: React.FC = () => {
  useInitOrg();
  const { theme } = useTheme();
  const { org } = useOrg();
  const location = useLocation();
  const [moreOpen, setMoreOpen] = useState(false);
  const layerRef = useRef<HTMLDivElement>(null);
  // Ionic components follow body.dark. Restore it on unmount: the legacy UI (Menu) expects it.
  // Layout effect: runs before first paint so Ionic never computes styles under the wrong theme.
  useLayoutEffect(() => {
    document.body.classList.toggle('dark', theme === 'dark');
  }, [theme]);
  useEffect(() => () => document.body.classList.add('dark'), []);

  const close = useCallback(() => setMoreOpen(false), []);
  const sheet = useMemo(
    () => ({ open: moreOpen, openSheet: () => setMoreOpen(true), close, toggle: () => setMoreOpen((o) => !o) }),
    [moreOpen, close],
  );
  // Any navigation closes the sheet.
  useEffect(() => setMoreOpen(false), [location.pathname]);
  // Leave the tab bar uncovered: the sheet's layer stops where the bar starts.
  useLayoutEffect(() => {
    const bar = document.querySelector('.su-app ion-tab-bar') as HTMLElement | null;
    if (moreOpen && layerRef.current && bar) layerRef.current.style.bottom = `${bar.offsetHeight}px`;
  }, [moreOpen]);

  return (
    <MoreSheetContext.Provider value={sheet}>
      <div className={`su-app ${themeClass(theme)}`} style={{ height: '100%' }}>
        <IonTabs>
          <IonRouterOutlet>
            <Route path="/app/home" exact component={HomePage} />
            <Route path="/app/network" exact component={NetworkPage} />
            <Route path="/app/states" exact render={() => <StateWisePage />} />
            <Route path="/app/state-cities" exact render={() => <StateWisePage drill />} />
            <Route path="/app/stores" exact component={StoresPage} />
            <Route path="/app/state-stores" exact component={StoresPage} />
            <Route path="/app/store/:siteCode" exact component={StoreDetailPage} />
            <Route path="/app" exact render={() => <Redirect to="/app/home" />} />
            <Route render={() => <Redirect to="/app/home" />} />
          </IonRouterOutlet>
          {/* Ionic 6 detects the tab bar via child.type === IonTabBar, so it must be inline (no wrapper component). */}
          <IonTabBar slot="bottom" className="su-tabbar">
            {SU_TABS.map((t) => (
              <IonTabButton key={t.tab} tab={t.tab} href={t.href} onClick={close}>
                <IonIcon icon={t.icon} />
                <IonLabel>{t.label}</IonLabel>
              </IonTabButton>
            ))}
            <MoreTabButton tab="more" open={moreOpen} onToggle={sheet.toggle} badge={org} />
          </IonTabBar>
        </IonTabs>
        {moreOpen && <MoreSheet onClose={close} layerRef={layerRef} />}
      </div>
    </MoreSheetContext.Provider>
  );
};

export default SuRoutes;
